// @ts-check
// Domínio próprio de envio em "Identidade e envio" (decisão P4 do usuário, item 2; contrato 09 de e-mail, seção 13):
// a escola informa o e-mail remetente do domínio dela (provedor gratuito é recusado, como no RD), o sistema cria a
// identidade do domínio na Amazon SES e mostra os registros para o DNS, com a situação "Aguardando DNS", "Verificado"
// ou "Falhou" e o "Verificar agora". Até o domínio ficar verificado, os e-mails saem do endereço padrão da plataforma.
// Mudar e remover pedem a flag WORKSPACE; conferir é de quem vê a tela.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { ApiError } from '../../data/api-client.js';
import { getSendingDomain, removeSendingDomain, setSendingDomain, verifySendingDomain } from '../../data/email.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { parseSenderEmail } from '../../../../shared/email/sending-domain.js';
import { formatDateTime } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/email.js').SendingDomain} SendingDomain */

const STATUS_TONES = Object.freeze({ PENDING: 'yellow', VERIFIED: 'green', FAILED: 'red' });

/**
 * Motivo da recusa do remetente em pt-BR (na tela antes de enviar e na resposta do servidor).
 * @param {unknown} error
 */
export function senderEmailErrorText(error) {
  if (!(error instanceof ApiError)) return null;
  const reason = typeof error.details?.reason === 'string' ? error.details.reason : null;
  if (error.code === 'INVALID_SENDER_EMAIL' && reason) return t(`emailSettings.domain.errors.${reason}`);
  if (error.code === 'SENDING_DOMAIN_IN_USE') return t('emailSettings.domain.errors.DOMAIN_IN_USE');
  if (error.code === 'SENDING_DOMAIN_PROVIDER_ERROR') return t('emailSettings.domain.errors.PROVIDER');
  return null;
}

/** Valor com o botão de copiar. @param {{ value: string, label: string }} props */
function CopyValue({ value, label }) {
  const [isCopied, setIsCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch {
      setIsCopied(false);
    }
  };
  return html`<span class="ec-copy-value">
    <code class="ec-mono ec-break">${value}</code>
    <${LightIconButton} icon=${isCopied ? 'IconCheck' : 'IconCopy'} ariaLabel=${`${isCopied ? t('emailSettings.domain.copied') : t('emailSettings.domain.copy')}: ${label}`} onClick=${copy} />
  </span>`;
}

/**
 * @param {{ platformAddress: string, onChanged?: (domain: SendingDomain | null) => void }} props
 */
export function SendingDomainSection({ platformAddress, onChanged }) {
  const canEdit = hasCurrentPermissionFlag('WORKSPACE');
  const [domain, setDomain] = useState(/** @type {SendingDomain | null | undefined} */ (undefined));
  const [senderEmail, setSenderEmail] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [busy, setBusy] = useState(/** @type {'SAVE' | 'VERIFY' | 'REMOVE' | null} */ (null));
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    getSendingDomain().then(setDomain).catch(() => setDomain(null));
  }, []);

  /** @param {SendingDomain | null} next */
  const update = (next) => {
    setDomain(next);
    onChanged?.(next);
  };

  const save = async () => {
    const parsed = parseSenderEmail(senderEmail, { platformDomains: [platformAddress.split('@')[1] ?? ''] });
    if ('problem' in parsed) {
      setError(t(`emailSettings.domain.errors.${parsed.problem}`));
      return;
    }
    setBusy('SAVE');
    setError(null);
    try {
      update(await setSendingDomain(parsed.senderEmail));
      setIsEditing(false);
      setSenderEmail('');
    } catch (requestError) {
      const text = senderEmailErrorText(requestError);
      if (text) setError(text);
      else showErrorSnackbar(requestError);
    } finally {
      setBusy(null);
    }
  };
  const verify = async () => {
    setBusy('VERIFY');
    try {
      const next = await verifySendingDomain();
      update(next);
      if (next) {
        showSuccessSnackbar(next.status === 'VERIFIED'
          ? t('emailSettings.domain.verifyResultVerified', { address: next.senderEmail })
          : next.status === 'FAILED' ? t('emailSettings.domain.verifyResultFailed') : t('emailSettings.domain.verifyResultPending'));
      }
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    setBusy('REMOVE');
    try {
      await removeSendingDomain();
      update(null);
      setConfirmRemove(false);
      showSuccessSnackbar(t('emailSettings.domain.removed'));
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setBusy(null);
    }
  };

  const form = html`<div class="ec-domain-form">
    <label class="ec-field">
      <span class="ec-field-label">${t('emailSettings.domain.senderEmail')}</span>
      <input class="text-input" type="email" value=${senderEmail} placeholder=${t('emailSettings.domain.senderEmailPlaceholder')} disabled=${!canEdit}
        data-testid="sending-domain-email" aria-invalid=${error ? 'true' : undefined}
        onInput=${(/** @type {Event} */ event) => {
          setSenderEmail(/** @type {HTMLInputElement} */ (event.currentTarget).value);
          setError(null);
        }}
        onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            save();
          }
        }} />
      ${error ? html`<span class="input-hint" data-danger="true" role="alert">${error}</span>` : html`<span class="input-hint">${t('emailSettings.domain.senderEmailHint')}</span>`}
    </label>
    <div class="ec-button-row">
      ${isEditing ? html`<${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${() => setIsEditing(false)} />` : null}
      <${Button} title=${t('emailSettings.domain.register')} size="small" accent="blue" disabled=${!canEdit || !senderEmail.trim()} isLoading=${busy === 'SAVE'} onClick=${save}
        data-testid="sending-domain-register" />
    </div>
  </div>`;

  return html`<section class="email-settings-section ec-domain-section" data-testid="sending-domain-section">
    <h3>${t('emailSettings.domain.title')}</h3>
    <p class="input-hint">${t('emailSettings.domain.intro', { address: platformAddress })}</p>
    ${!canEdit ? html`<p class="input-hint"><${Icon} name="IconLock" size=${12} /> ${t('emailSettings.domain.readOnly')}</p>` : null}
    ${domain === undefined
      ? html`<${Skeleton} height=${64} radius=${8} />`
      : !domain || isEditing
        ? form
        : html`<div class="ec-domain-state">
            <div class="ec-domain-header">
              <span class="ec-mono">${domain.senderEmail}</span>
              <span class="ec-badge" data-tone=${STATUS_TONES[domain.status] ?? 'gray'} data-testid="sending-domain-status">${t(`emailSettings.domain.status.${domain.status}`)}</span>
              ${domain.lastCheckedAt ? html`<span class="ec-muted">${t('emailSettings.domain.lastChecked', { date: formatDateTime(domain.lastCheckedAt) })}</span>` : null}
            </div>
            <p class="input-hint">${t(`emailSettings.domain.statusHint.${domain.status}`, { address: domain.senderEmail })}</p>
            <div class="ec-button-row">
              <${Button} title=${t('emailSettings.domain.verify')} icon="IconRefresh" size="small" variant="secondary" isLoading=${busy === 'VERIFY'} onClick=${verify}
                data-testid="sending-domain-verify" />
              ${canEdit
                ? html`<${Button} title=${t('emailSettings.domain.change')} size="small" variant="secondary" onClick=${() => {
                    setSenderEmail(domain.senderEmail);
                    setIsEditing(true);
                  }} />
                  <${Button} title=${t('emailSettings.domain.remove')} icon="IconTrash" size="small" variant="secondary" accent="danger" onClick=${() => setConfirmRemove(true)} />`
                : null}
            </div>
            ${domain.status !== 'VERIFIED' || domain.records.some((record) => !record.isRequired)
              ? html`<div class="ec-dns">
                  <h4 class="ec-section-title">${t('emailSettings.domain.recordsTitle')}</h4>
                  <p class="input-hint">${t('emailSettings.domain.recordsHint')}</p>
                  <ul class="ec-dns-records" data-testid="sending-domain-records">
                    ${domain.records.map((record, index) => html`<li key=${`${record.kind}-${index}`} class="ec-dns-record">
                      <div class="ec-dns-record-header">
                        <span class="ec-badge" data-tone="gray">${record.type}</span>
                        <strong>${t(`emailSettings.domain.recordKinds.${record.kind}`)}</strong>
                        <span class="ec-muted">${record.isRequired ? t('emailSettings.domain.required') : t('emailSettings.domain.recommended')}</span>
                      </div>
                      <div class="ec-dns-record-row"><span class="ec-muted">${t('emailSettings.domain.recordColumns.name')}</span><${CopyValue} value=${record.name} label=${t('emailSettings.domain.recordColumns.name')} /></div>
                      <div class="ec-dns-record-row"><span class="ec-muted">${t('emailSettings.domain.recordColumns.value')}</span><${CopyValue} value=${record.value} label=${t('emailSettings.domain.recordColumns.value')} /></div>
                    </li>`)}
                  </ul>
                </div>`
              : null}
          </div>`}
    ${confirmRemove && domain
      ? html`<${Modal} title=${t('emailSettings.domain.removeTitle', { domain: domain.domain })} subtitle=${t('emailSettings.domain.removeDescription', { address: platformAddress })}
          confirmLabel=${t('emailSettings.domain.remove')} confirmAccent="danger" isLoading=${busy === 'REMOVE'} onClose=${() => setConfirmRemove(false)} onConfirm=${remove} />`
      : null}
  </section>`;
}
